/** Written in place of a query value the allowlist does not name, the invitation key included. */
export const REDACTED = 'REDACTED';

/** Query parameters whose values reach telemetry unchanged, lower-cased; the API's `TelemetryRedaction` keeps the same list. */
export const ALLOWED_QUERY_PARAMETERS: ReadonlySet<string> = new Set([
  'isprovider',
  'recreate',
  'take',
  'lastloadedsessionid',
  'clears',
  'missingitemformid',
  'clientid',
  'credentialingcycleid',
  'lastupdated',
  'jobid',
  'shortstatus',
  'api-version',
  'comp',
  'restype',
]);

const EMAIL_PATTERN = /[a-z0-9._%+'-]+(?:@|%40)[a-z0-9.-]+\.[a-z]{2,}/gi;
const DECODE_PATTERN = /(\/AccountController\/decode\/)[^/?#\s]+/gi;
const INVITE_PATTERN = /(\/inviteProvider\/)[^/?#\s]+/gi;
// Only as the first path segment (string start, after whitespace, or right after scheme and host), so API paths keep their /l/.
const SHORT_LINK_PATTERN = /(^|\s|[a-z][a-z0-9+.-]*:\/\/[^/?#\s]*)(\/l\/)[^/?#\s]+/gi;
// A query ends at '#', whitespace or the end, so a URL inside a message or a stack trace ends where its token does.
const QUERY_PATTERN = /\?([^#\s]+)/g;
const GUID_PREFIX_PATTERN = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/i;

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed escape is still matched by its raw text.
    return value;
  }
};

const scrubFileName = (value: string): string => {
  const guid = GUID_PREFIX_PATTERN.exec(safeDecode(value))?.[1];
  return guid ? `${guid}-{file}` : REDACTED;
};

const scrubParameter = (parameter: string): string => {
  const separator = parameter.indexOf('=');
  const rawName = separator < 0 ? parameter : parameter.slice(0, separator);
  const value = separator < 0 ? '' : parameter.slice(separator + 1);
  const name = safeDecode(rawName).toLowerCase();

  if (ALLOWED_QUERY_PARAMETERS.has(name)) {
    return parameter;
  }
  if (separator < 0) {
    // A bare token could itself be a value.
    return REDACTED;
  }
  if (value === '') {
    return parameter;
  }
  if (name === 'realname') {
    return `${rawName}=${scrubFileName(value)}`;
  }
  // The key (no fingerprint here: initializers are synchronous, Web Crypto is not), names, emails, secrets.
  return `${rawName}=${REDACTED}`;
};

/** Replaces email addresses, raw or %40-encoded, with `{email}`. */
export function scrubText(value: string): string {
  return value.replace(EMAIL_PATTERN, '{email}');
}

/** Rewrites every query string: allowlisted values stay, `RealName` keeps its GUID, every other value becomes `REDACTED`. */
export function scrubQuery(value: string): string {
  return value.replace(
    QUERY_PATTERN,
    (_match, query: string) => `?${query.split('&').map(scrubParameter).join('&')}`,
  );
}

/** Replaces the short code and the invitee email in their path segments. */
export function scrubPath(value: string): string {
  return value
    .replace(DECODE_PATTERN, '$1{key}')
    .replace(INVITE_PATTERN, '$1{providerEmail}')
    .replace(SHORT_LINK_PATTERN, '$1$2:code');
}

/** Applies the path rules, then the query rules, then the email pattern. */
export function scrubUrl(value: string): string {
  return scrubText(scrubQuery(scrubPath(value)));
}
